import React, { useState, useEffect, useRef } from "react";
import StepHeader from "./StepHeader";
import SecurePaymentIcon from '../../assets/icons/fbs/SecurePaymentIcon.png';
import MoneyBack from '../../assets/icons/fbs/MoneyBack.png';
import arrow from '../../assets/icons/fbs/ArrowDown.svg';
import {
    useStripe,
    useElements,
    PaymentElement,
    Elements
} from '@stripe/react-stripe-js';
import { loadStripe, PaymentIntentResult, SetupIntentResult } from '@stripe/stripe-js';
import axios from 'axios';
import { API_V1_PREFIX, isProd } from '../../utils/constants';
import { toast } from 'react-toastify';
import { getProductType, getPageType, getCheckoutStep, getUTMs, fireDiscountApplied, fireDiscountRemoved } from '../../utils/dataLayer';

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '');

type IntentMode = 'payment' | 'setup';

const CheckoutForm = ({ onComplete, memberDetails, accessToken, intentMode }: {
    onComplete: () => void;
    memberDetails?: any;
    accessToken: string | null;
    intentMode: IntentMode;
}) => {
    const stripe = useStripe();
    const elements = useElements();
    const [error, setError] = useState<string | null>(null);
    const [processing, setProcessing] = useState(false);
    const [cardholderName, setCardholderName] = useState('');
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState('card');

    useEffect(() => {
        if (memberDetails?.first_name && memberDetails?.last_name && !cardholderName) {
            setCardholderName(`${memberDetails.first_name} ${memberDetails.last_name}`);
        }
    }, [memberDetails, cardholderName]);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!stripe || !elements) return;

        setProcessing(true);
        try {
            // Persist a minimal "pending purchase" payload for redirect-based Stripe flows (e.g. 3DS),
            // where this component may not get a chance to fire `purchase` before navigating.
            try {
                if (accessToken) {
                    const amountStr = localStorage.getItem('subscriptionAmount');
                    const amount = amountStr != null ? Number(amountStr) : null;
                    sessionStorage.setItem(
                        `pending_purchase_${accessToken}`,
                        JSON.stringify({
                            amount,
                            membership_type: memberDetails?.membership_type || 'SCAN',
                        })
                    );
                }
            } catch {
                // best-effort
            }

            if (intentMode === 'setup') {
                const result = (await stripe.confirmSetup({
                    elements,
                    redirect: 'if_required',
                    confirmParams: {
                        return_url: `${window.location.origin}/complete-onboarding?token=${accessToken}`,
                    },
                })) as SetupIntentResult;

                if (result.error) {
                    setError(result.error.message || 'An error occurred');
                } else if (result.setupIntent) {
                    if (result.setupIntent.status === 'succeeded') {
                        onComplete();
                    } else {
                        setError(`Card setup ${result.setupIntent.status}. Please try again.`);
                    }
                }
            } else {
                const result = (await stripe.confirmPayment({
                    elements,
                    redirect: 'if_required',
                    confirmParams: {
                        return_url: `${window.location.origin}/complete-onboarding?token=${accessToken}`,
                    },
                })) as PaymentIntentResult;

                if (result.error) {
                    setError(result.error.message || 'An error occurred');
                } else if (result.paymentIntent) {
                    if (result.paymentIntent.status === 'succeeded') {
                        onComplete();
                    } else {
                        setError(`Payment ${result.paymentIntent.status}. Please try again.`);
                    }
                }
            }
        } catch (err) {
            console.error('Error processing payment:', err);
            setError('Failed to process payment. Please try again.');
        } finally {
            setProcessing(false);
        }
    };

    const showCardholderName = selectedPaymentMethod === 'card';

    return (
        <form onSubmit={handleSubmit}>
            {showCardholderName && (
                <div style={{ marginBottom: '16px' }}>
                    <label style={{ display: 'block', marginBottom: '6px', fontSize: '14px', fontWeight: 500 }}>Cardholder name</label>
                    <input
                        type="text"
                        className="input-field"
                        placeholder="Full name on card"
                        value={cardholderName}
                        onChange={(e) => setCardholderName(e.target.value)}
                        style={{ width: '100%', padding: '12px', borderRadius: '8px', border: '1px solid #1212123D' }}
                    />
                </div>
            )}
            <PaymentElement
                onChange={(e) => {
                    const paymentMethodType = e.value?.type || 'card';
                    setSelectedPaymentMethod(paymentMethodType);
                }}
            />
            <button
                type="submit"
                className="black-btn"
                disabled={!stripe || processing}
                style={{ marginTop: '20px' }}
            >
                {processing ? 'Processing...' : 'Complete booking'}
            </button>
            {error && <div style={{ color: '#dc2626', marginTop: '12px', fontSize: '14px' }}>{error}</div>}
        </form>
    );
};

const Step3 = () => {
    const [showDiscountCode, setShowDiscountCode] = useState(false);
    const [clientSecret, setClientSecret] = useState<string | null>(null);
    const [intentMode, setIntentMode] = useState<IntentMode>('payment');
    const [accessToken, setAccessToken] = useState<string | null>(null);
    const [subscriptionAmount, setSubscriptionAmount] = useState<number | null>(null);
    const [memberDetails, setMemberDetails] = useState<any>(null);
    const [isLoading, setIsLoading] = useState(true);
    let storedAppliedPromocode = '';
    try {
        storedAppliedPromocode = localStorage.getItem('appliedPromocode') || '';
    } catch {}

    const [promocode, setPromocode] = useState(storedAppliedPromocode);
    const [couponApplied, setCouponApplied] = useState(!!storedAppliedPromocode.trim());
    const hasInitialized = useRef(false);

    const baseURL = isProd
        ? process.env.REACT_APP_API_BASE_URL_PROD
        : process.env.REACT_APP_API_BASE_URL_DEV;

    const completeFreePurchase = (amount: number) => {
        const token = accessToken;
        if (!token) return;

        const path = window.location.pathname;
        const membershipType = memberDetails?.membership_type || 'SCAN';
        const dedupeKey = `purchase_fired_${token}`;
        if (!sessionStorage.getItem(dedupeKey)) {
            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                event: 'purchase',
                url: window.location.href,
                host: window.location.host,
                path,
                product_type: getProductType(path, membershipType),
                page_type: getPageType(path),
                checkout_step: getCheckoutStep(path),
                ...getUTMs(),
                referrer: document.referrer || null,
                member_details: memberDetails,
                membership_type: membershipType,
                amount,
            });
            sessionStorage.setItem(dedupeKey, '1');
        }

        localStorage.removeItem('signupResponse');
        localStorage.removeItem('bookingData');
        localStorage.removeItem('appliedPromocode');
        localStorage.removeItem('selectedPlan');
        localStorage.removeItem('subscriptionAmount');

        window.location.href = `/complete-onboarding?token=${token}`;
    };

    useEffect(() => {
        if (hasInitialized.current) return;
        hasInitialized.current = true;

        const signupResponseStr = localStorage.getItem('signupResponse');
        if (!signupResponseStr) {
            toast.error('Signup data not found. Please go back and try again.');
            setIsLoading(false);
            return;
        }

        let signupData: any;
        try {
            signupData = JSON.parse(signupResponseStr);
        } catch {
            toast.error('Invalid signup data.');
            setIsLoading(false);
            return;
        }

        const token = signupData.access_token;
        setAccessToken(token);

        if (signupData.client_secret) {
            setClientSecret(signupData.client_secret);
            setIntentMode('payment');
        } else if (signupData.setup_intent_client_secret) {
            setClientSecret(signupData.setup_intent_client_secret);
            setIntentMode('setup');
        }
        if (signupData.amount != null) {
            setSubscriptionAmount(signupData.amount);
        }

        if (!token) {
            toast.error('Access token not found. Please go back and try again.');
            setIsLoading(false);
            return;
        }

        const initPayment = async () => {
            try {
                
                await axios.get(
                    `${baseURL}/${API_V1_PREFIX}subscriptions/check-active`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                
                const memberResponse = await axios.get(
                    `${baseURL}/${API_V1_PREFIX}members/?load_strategy=with_subscription`,
                    { headers: { Authorization: `Bearer ${token}` } }
                );
                setMemberDetails(memberResponse.data);

              
                const selectedPlanStr = localStorage.getItem('selectedPlan');
                let productId: string | null = null;
                if (selectedPlanStr) {
                    try {
                        const selectedPlan = JSON.parse(selectedPlanStr);
                        productId = selectedPlan.productId || selectedPlan.product?.stripe_price_yearly_id || selectedPlan.product?.stripe_price_monthly_id;
                    } catch {}
                }

                const storedPromocode = localStorage.getItem('appliedPromocode');
                const payload: any = {};
                if (productId) payload.product_id = productId;
                if (storedPromocode?.trim()) payload.promocode = storedPromocode.trim();

                const subsResponse = await axios.post(
                    `${baseURL}/${API_V1_PREFIX}subscriptions`,
                    payload,
                    { headers: { Authorization: `Bearer ${token}` } }
                );

                if (subsResponse.data.client_secret) {
                    setClientSecret(subsResponse.data.client_secret);
                    setIntentMode('payment');
                } else if (subsResponse.data.setup_intent_client_secret) {
                    setClientSecret(subsResponse.data.setup_intent_client_secret);
                    setIntentMode('setup');
                }
                if (subsResponse.data.amount != null) {
                    setSubscriptionAmount(subsResponse.data.amount);
                }

                // If discount makes total free, backend may not return an invoice/client_secret.
                // In that case we should still capture a card via SetupIntent for future charges.
                const amount = subsResponse.data.amount;
                if ((amount === 0 || amount === '0') && !subsResponse.data.client_secret && !subsResponse.data.setup_intent_client_secret) {
                    completeFreePurchase(0);
                    return;
                }
            } catch (error: any) {
                console.error('Error initializing payment:', error);
                if (error?.response?.data?.detail) {
                    toast.error(error.response.data.detail);
                } else if (error?.response?.data?.message) {
                    toast.error(error.response.data.message);
                }
            } finally {
                setIsLoading(false);
            }
        };

        initPayment();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [baseURL]);

    const handleApplyCoupon = async () => {
        if (!promocode.trim()) {
            toast.error('Please enter a discount code');
            return;
        }
        if (!accessToken) return;

        try {
            const selectedPlanStr = localStorage.getItem('selectedPlan');
            let productId: string | null = null;
            if (selectedPlanStr) {
                try {
                    const selectedPlan = JSON.parse(selectedPlanStr);
                    productId = selectedPlan.productId || selectedPlan.product?.stripe_price_yearly_id || selectedPlan.product?.stripe_price_monthly_id;
                } catch {}
            }

            const payload: any = { promocode: promocode.trim() };
            if (productId) payload.product_id = productId;

            const response = await axios.post(
                `${baseURL}/${API_V1_PREFIX}subscriptions`,
                payload,
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );

            if (response.data.client_secret) {
                setClientSecret(response.data.client_secret);
                setIntentMode('payment');
            } else if (response.data.setup_intent_client_secret) {
                setClientSecret(response.data.setup_intent_client_secret);
                setIntentMode('setup');
            }
            if (response.data.amount != null) {
                setSubscriptionAmount(response.data.amount);
            }

            setCouponApplied(true);
            localStorage.setItem('appliedPromocode', promocode.trim());
            fireDiscountApplied(promocode.trim());
            toast.success('Discount code applied!');
        } catch (error: any) {
            if (error?.response?.data?.detail) {
                toast.error(error.response.data.detail);
            } else {
                toast.error('Failed to apply discount code.');
            }
        }
    };

    const handleRemoveCoupon = async () => {
        if (!accessToken) return;

        try {
            const selectedPlanStr = localStorage.getItem('selectedPlan');
            let productId: string | null = null;
            if (selectedPlanStr) {
                try {
                    const selectedPlan = JSON.parse(selectedPlanStr);
                    productId = selectedPlan.productId || selectedPlan.product?.stripe_price_yearly_id || selectedPlan.product?.stripe_price_monthly_id;
                } catch {}
            }

            const payload: any = {};
            if (productId) payload.product_id = productId;

            const response = await axios.post(
                `${baseURL}/${API_V1_PREFIX}subscriptions`,
                payload,
                { headers: { Authorization: `Bearer ${accessToken}` } }
            );

            if (response.data.client_secret) {
                setClientSecret(response.data.client_secret);
                setIntentMode('payment');
            } else if (response.data.setup_intent_client_secret) {
                setClientSecret(response.data.setup_intent_client_secret);
                setIntentMode('setup');
            }
            if (response.data.amount != null) {
                setSubscriptionAmount(response.data.amount);
            }

            const removedCode = promocode.trim();
            setCouponApplied(false);
            setPromocode('');
            localStorage.removeItem('appliedPromocode');
            fireDiscountRemoved(removedCode);
            toast.success('Discount code removed');
        } catch (error: any) {
            toast.error('Failed to remove discount code.');
        }
    };

    const handlePaymentComplete = () => {
        const path = window.location.pathname;
        const membershipType = memberDetails?.membership_type || 'SCAN';

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            event: 'purchase',
            url: window.location.href,
            host: window.location.host,
            path,
            product_type: getProductType(path, membershipType),
            page_type: getPageType(path),
            checkout_step: getCheckoutStep(path),
            ...getUTMs(),
            referrer: document.referrer || null,
            member_details: memberDetails,
            membership_type: membershipType,
            amount: subscriptionAmount
        });

        localStorage.removeItem('signupResponse');
        localStorage.removeItem('bookingData');
        localStorage.removeItem('appliedPromocode');
        localStorage.removeItem('selectedPlan');
        localStorage.removeItem('subscriptionAmount');

        window.location.href = `/complete-onboarding?token=${accessToken}`;
    };

  
    const formatSummaryBookingDate = (dateValue: string): string => {
        if (!dateValue) return '';
        if (dateValue === 'ASAP') return 'ASAP';
        if (dateValue === 'Flexible' || dateValue === 'Flexible Date') return 'Flexible';

        // Expected iso format from dropdown: YYYY-MM-DD
        const isoMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateValue);
        if (isoMatch) {
            const [, yyyy, mm, dd] = isoMatch;
            return `${dd}/${mm}/${yyyy}`;
        }

        return dateValue;
    };

    let bookingCentre = 'Harley Street';
    let bookingDate = '';

    const bookingDataStr = localStorage.getItem('bookingData');
    if (bookingDataStr) {
        try {
            const bookingData = JSON.parse(bookingDataStr);
            bookingDate = bookingData.desire_date || '';
            bookingCentre = bookingData.site_name || bookingData.siteName || bookingCentre;
        } catch {}
    }

    const selectedPlanStr = localStorage.getItem('selectedPlan');
    const scanTypeLabel = 'Full-body MRI';
    let addOnLabel = 'Scan';
    if (selectedPlanStr) {
        try {
            const selectedPlan = JSON.parse(selectedPlanStr);
            const productKey: string | undefined = selectedPlan?.product?.key;
            if (productKey === 'full_body_lite') {
                addOnLabel = 'Scan Lite';
            }
            // default: full_body_mri => "Scan"
        } catch {}
    }

    const bookingDateFormatted = bookingDate ? formatSummaryBookingDate(bookingDate) : '';

    const displayAmount = subscriptionAmount != null
        ? `£${subscriptionAmount % 1 === 0 ? Math.round(subscriptionAmount).toLocaleString() : subscriptionAmount.toFixed(2)}`
        : '£1,650';

    const stripeOptions = React.useMemo(
        () => clientSecret
            ? {
                clientSecret,
                appearance: {
                    theme: 'stripe' as const,
                    variables: {
                        colorPrimary: '#163b21',
                        colorBackground: '#ffffff',
                        colorText: '#0C2012',
                        colorDanger: '#dc2626',
                        fontFamily: 'Satoshi, sans-serif',
                        spacingUnit: '4px',
                        borderRadius: '8px',
                    },
                },
            }
            : undefined,
        [clientSecret]
    );

    return (
        <div className="inner-container2" >
            <StepHeader stepCount="Step 3 of 3" title="Secure payment" stepProgress={100} />
            <div className="horizontal-gap1" >
                <div className="horizontal-gap2" >
                    <div className="discount-code-container" >
                        <div onClick={() => setShowDiscountCode(!showDiscountCode)} className="discount-code-header" >
                            <span>Discount code</span>
                            <img className={showDiscountCode ? 'rotate-180' : ''} src={arrow} alt="Arrow" />
                        </div>
                        {showDiscountCode && <div className="discount-code-input-wrapper" >
                            <input
                                type="text"
                                placeholder="Enter code"
                                className="input-field"
                                value={promocode}
                                onChange={(e) => setPromocode(e.target.value)}
                                disabled={couponApplied}
                            />
                            {!couponApplied ? (
                                <button className="apply-btn" onClick={handleApplyCoupon}>Apply</button>
                            ) : (
                                <button className="remove-btn" onClick={handleRemoveCoupon}>Remove</button>
                            )}
                        </div>}
                    </div>

                    <div style={{ minHeight: "294px", borderRadius: "8px" }}>
                        {isLoading && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '294px', background: '#EFEFEF', borderRadius: '8px' }}>
                                Loading payment form...
                            </div>
                        )}
                        {!isLoading && clientSecret && stripeOptions && (
                            <Elements key={clientSecret} stripe={stripePromise} options={stripeOptions}>
                                <CheckoutForm
                                    onComplete={handlePaymentComplete}
                                    memberDetails={memberDetails}
                                    accessToken={accessToken}
                                    intentMode={intentMode}
                                />
                            </Elements>
                        )}
                        {!isLoading && !clientSecret && (
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '294px', background: '#EFEFEF', borderRadius: '8px' }}>
                                Unable to load payment form. Please try again.
                            </div>
                        )}
                    </div>

                    <div className="plan-info-row" >
                        <div className="plan-info-row-item" >
                            <div className="gray-card" >
                                <h4>Order summary</h4>
                                <div className="order-summary-content" >
                                    <div className="key-value-row" >
                                        <span className="key" >Scan type:</span>
                                        <span className="value" >{scanTypeLabel}</span>
                                    </div>
                                    <div className="key-value-row" >
                                        <span className="key" >Plan Type:</span>
                                        <span className="value" >{addOnLabel}</span>
                                    </div>
                                    <div className="key-value-row" >
                                        <span className="key" >Centre:</span>
                                        <span className="value" >{bookingCentre}</span>
                                    </div>
                                    {bookingDateFormatted && <div className="key-value-row" >
                                        <span className="key" >Date:</span>
                                        <span className="value" >{bookingDateFormatted}</span>
                                    </div>}
                                    <div className="key-value-row" >
                                        <span className="key" >Price:</span>
                                        <span className="value" >{displayAmount}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="right-col horizontal-gap2" >
                            <div className="gray-card" >
                                <h3>Your booking includes</h3>
                                <ul>
                                    <li>Complete full-body health screening</li>
                                    <li>Expert radiologist report within 3 days</li>
                                    <li>2x GP consultations</li>
                                    <li>CQC registered & fully accredited</li>
                                </ul>
                            </div>
                            <div>
                                <div className="money-back-guarantee-card" >
                                    <img src={MoneyBack} alt="" />
                                    <div>
                                        <h5>Money-back guarantee</h5>
                                        <div>Full refund if scan cancelled 48+ hours in advance</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
                <div className="text-center" >
                    <img src={SecurePaymentIcon} alt="Secure payment" width={230} height={38} />
                </div>
            </div>
        </div>
    )
}

export default Step3;
